import type { PaletteColor } from "@sc07-canvas/lib/net";
import EventEmitter from "eventemitter3";

import { CanvasCore } from "./canvas";
import type { ExtractMethods } from "./utils";

type GetMethodFromClass<
  T extends new (...args: any) => any,
  K,
> = K extends keyof T ? T[K] : never;

const _static = <
  Clazz extends (new (...args: any) => any) & {
    getInstance: () => InstanceType<Clazz>;
  },
  Method extends keyof ExtractMethods<InstanceType<Clazz>>,
>(
  clazz: Clazz,
  method: Method,
): GetMethodFromClass<InstanceType<Clazz>, Method> => {
  const instance = clazz.getInstance();
  return instance[method].bind(instance);
};

interface PaletteEvents {
  selectedColor(color: number | undefined): void;
  secondaryColor(color: number | undefined): void;
}

export class PaletteLib extends EventEmitter<PaletteEvents> {
  #selectedColor?: number;
  #secondaryColor?: number;

  getColor(colorId: number): PaletteColor | undefined {
    const config = CanvasCore.get().getConfig();
    return config.pallete.colors.find((c) => c.id === colorId);
  }

  setSelectedColor(color: number | undefined) {
    this.#selectedColor = color;
    this.emit("selectedColor", color);
  }

  getSelectedColor(): PaletteColor | undefined {
    if (this.#selectedColor === undefined) return undefined;
    return this.getColor(this.#selectedColor);
  }

  setSecondaryColor(color: number | undefined) {
    this.#secondaryColor = color;
    this.emit("secondaryColor", color);
  }

  getSecondaryColor(): PaletteColor | undefined {
    if (this.#secondaryColor === undefined) return undefined;
    return this.getColor(this.#secondaryColor);
  }

  swapSelectedColors() {
    const selectedColor = this.#selectedColor;
    const secondaryColor = this.#secondaryColor;

    this.#selectedColor = secondaryColor;
    this.#secondaryColor = selectedColor;
    this.emit("selectedColor", this.#selectedColor);
    this.emit("secondaryColor", this.#secondaryColor);
  }

  getColorFromHex(hex: string): PaletteColor | undefined {
    const normalizedHex = hex.trim().replace(/^#/, "").toLowerCase();
    if (!normalizedHex || normalizedHex === "null") return undefined;

    const config = CanvasCore.get().getConfig();
    return config.pallete.colors.find(
      (c) => c.hex.trim().replace(/^#/, "").toLowerCase() === normalizedHex,
    );
  }

  // -- hoist to static --

  private static instance: PaletteLib;
  static getInstance() {
    if (this.instance) return this.instance;
    return (this.instance = new PaletteLib());
  }
  static getColorFromHex = _static(PaletteLib, "getColorFromHex");
  static setSelectedColor = _static(PaletteLib, "setSelectedColor");
  static getSelectedColor = _static(PaletteLib, "getSelectedColor");
  static setSecondaryColor = _static(PaletteLib, "setSecondaryColor");
  static getSecondaryColor = _static(PaletteLib, "getSecondaryColor");
  static swapSelectedColors = _static(PaletteLib, "swapSelectedColors");
  static getColor = _static(PaletteLib, "getColor");
}
